// ============================================================================
//  Velcor pricing — single source of truth
// ----------------------------------------------------------------------------
//  This module is the ONLY place where plan rates, service usage anchors,
//  surcharges, the dynamic ZIP+TDSP variance and the Secure 24 floor live.
//
//  Why centralized:
//    • Public PlanPage.jsx (customer hero) and admin ServicePlacement.jsx
//      (agent placement flow) must always show identical $ amounts for the
//      same address. Any drift = the customer hears one price online and
//      another one on the phone — disaster for trust.
//    • Plan bullets and agent talking-points also belong here so copy stays
//      consistent across surfaces.
//
//  Public API (frontend only — backend has its own rate sheet):
//    • SERVICE_USAGE_KWH        — { apartments: 1000, residential: 2000, commercial: 3000 }
//    • PLAN_BASE                — Plan metadata: id, baseRate, term, eyebrow, name, bullets, description
//    • PLAN_AGENT_PITCH         — Internal sales playbook (admin-only)
//    • PROFILE_LABELS           — Friendly labels for RentCast detection banner
//    • hash01(seed)             — Deterministic FNV-1a 0..1 hash
//    • dynamicRate(plan, ...)   — ZIP+TDSP variance + service surcharge
//    • zoneAverageRate(...)     — Reference average for the area
//    • computePricings(...)     — Computes all 3 plans + applies $15 secure24 floor
// ============================================================================

// kWh anchor per service type — what we quote the monthly $ at.
export const SERVICE_USAGE_KWH = {
  apartments:  1000,
  residential: 2000,
  commercial:  3000,
};

// Friendly labels for the RentCast auto-detection banner.
export const PROFILE_LABELS = {
  apt_studio: "Estudio / eficiencia",
  apt_1br:    "Apartamento de 1 habitación",
  apt_2br:    "Apartamento de 2+ habitaciones",
  res_small:  "Casa pequeña (1–2 hab)",
  res_med:    "Casa mediana (3 hab)",
  res_large:  "Casa grande (4+ hab)",
};

// 3 unified plans across every service type. The customer's serviceType
// affects pricing through `dynamicRate` (surcharge) and `usage` anchor,
// but the catalog is one.
//
// `bullets` mirrors what the customer reads on PlanPage.jsx and on the
// admin's AgentPlanModal — same list both surfaces.
export const PLAN_BASE = [
  {
    id: "saver12",
    name: "Velcor Saver 12",
    term: "12 meses",
    termEn: "12 months",
    termMonths: 12,
    baseRate: 10.9,
    accent: false,
    eyebrow: "PRECIO MÁS BAJO",
    eyebrowEn: "LOWEST PRICE",
    description: "Nuestra tarifa más baja con precio fijo por 12 meses.",
    iconName: "ShieldCheck",
    autopayCredit: 5,
    bullets: [
      "Tarifa fija por 12 meses",
      "El precio más bajo de Velcor garantizado",
      "Sin cargo mensual base ni cuota de conexión",
      "Sin sorpresas — sin letra chica en tu factura",
      "Cancela cuando quieras, sin penalidad",
      "Soporte bilingüe 7 días/semana",
      "Comparador anual gratis para renovar al mejor precio",
    ],
  },
  {
    id: "eco12",
    name: "Velcor Eco 12",
    term: "12 meses",
    termEn: "12 months",
    termMonths: 12,
    baseRate: 12.4,
    accent: true,
    eyebrow: "RECOMENDADO · 100% RENOVABLE",
    eyebrowEn: "RECOMMENDED · 100% RENEWABLE",
    description: "100% energía solar y eólica de Texas. Tarifa fija 12 meses.",
    iconName: "Leaf",
    autopayCredit: 5,
    bullets: [
      "Tarifa fija por 12 meses",
      "100% energía renovable certificada (REC)",
      "Apoya granjas eólicas y solares en Texas",
      "Crédito de $10 en tu primera factura",
      "Sin cargo mensual base",
      "Cancela cuando quieras, sin penalidad",
      "Soporte bilingüe premium",
      "Reporte mensual de tu huella de carbono",
    ],
  },
  {
    id: "secure24",
    name: "Velcor Secure 24",
    term: "24 meses",
    termEn: "24 months",
    termMonths: 24,
    baseRate: 11.9,
    accent: false,
    eyebrow: "MAYOR ESTABILIDAD",
    eyebrowEn: "MOST STABLE",
    description: "Tarifa bloqueada por 24 meses — máxima protección de precio.",
    iconName: "Star",
    autopayCredit: 10,
    bullets: [
      "Tarifa fija por 24 meses — el contrato más largo",
      "🎁 1 mes de energía GRATIS al cumplir el primer año",
      "Inmune a 2 temporadas de subidas del mercado",
      "Account manager dedicado para tu cuenta",
      "Crédito de $25 en tu primera factura",
      "Bono de lealtad: $100 al cumplir 24 meses",
      "Reporting mensual de ahorro vs mercado",
      "Cancela cuando quieras, sin penalidad",
      "Soporte bilingüe premium 24/7 con línea directa",
    ],
  },
];

// Convenience lookup by id.
export const PLAN_BY_ID = Object.fromEntries(PLAN_BASE.map((p) => [p.id, p]));

// Internal sales playbook — only rendered inside the admin's AgentPlanModal.
// Customers never see this. Each plan has a pitch list + objections list.
export const PLAN_AGENT_PITCH = {
  saver12: {
    headline: "Plan de entrada — el precio más bajo gana la venta",
    pitch: [
      "“Te garantizo el precio más bajo de Velcor por 12 meses. Si encuentras algo mejor, cancelas sin penalidad.”",
      "Ideal para familias con presupuesto justo, primeros clientes y quienes vienen de planes variables.",
      "Refuerza: la tarifa NO cambia ni un centavo durante un año entero — diferenciador clave vs prepago.",
    ],
    objections: [
      { q: "“¿Y si suben los precios del mercado?”", a: "“Tu tarifa queda bloqueada. Aunque suba el wholesale, tu factura no se mueve.”" },
      { q: "“No quiero comprometerme a 12 meses.”", a: "“Es contrato flexible. Cancelas cuando quieras y no pagas nada.”" },
      { q: "“Mi vecino tiene 8¢ con otra compañía.”", a: "“Pregunta el TDU charge — casi siempre nuestro precio total es menor cuando sumas todo.”" },
    ],
  },
  eco12: {
    headline: "Plan recomendado — 100% renovable + crédito $10",
    pitch: [
      "“Por cada cliente Eco evitamos ~5 toneladas de CO₂ al año — equivalente a plantar 100 árboles.”",
      "Subraya el crédito de $10 en la primera factura para arrancar con descuento.",
      "Diferenciador: la luz que entra a tu casa es idéntica, lo único que cambia es de dónde viene.",
      "Vendedor de cara a clientes jóvenes / familias con hijos / inquilinos con conciencia ambiental.",
    ],
    objections: [
      { q: "“¿Por qué cuesta más que el Saver?”", a: "“Pagas un poco más por kWh para apoyar granjas eólicas y solares de Texas. Por eso te damos $10 de crédito en tu primera factura.”" },
      { q: "“¿Funciona igual?”", a: "“Es exactamente la misma luz por el mismo cable. La diferencia está en de dónde la producimos.”" },
      { q: "“¿Está certificado?”", a: "“Sí, RECs verificados por terceros. Te enviamos el certificado mensual con tu factura.”" },
    ],
  },
  secure24: {
    headline: "Plan premium — máxima protección de precio + lealtad",
    pitch: [
      "“Bloqueas tu tarifa por 2 AÑOS completos. Cero subidas, cero sorpresas — duermes tranquilo.”",
      "Bono insignia: 1 MES GRATIS al cumplir el año + $100 bonus al cumplir los 24 meses.",
      "Account manager dedicado — un número directo, sin call center. Es nuestro VIP.",
      "Refuerza: en TX el promedio sube ~12% por temporada de calor. Con Secure 24 evitas 2 temporadas de subida.",
    ],
    objections: [
      { q: "“24 meses es mucho tiempo.”", a: "“Cancelas cuando quieras, sin penalidad. El 24 es tu protección, no tu cadena.”" },
      { q: "“Es más caro que el Saver.”", a: "“Sí, ~$15 más al mes. Recibes 1 mes gratis (~$100) + $100 bonus al renovar = $200 de ahorro garantizado. Es ganancia neta.”" },
      { q: "“¿Y si encuentro algo mejor?”", a: "“Cancelas y te vas. Pero el Secure suele ser el mejor del mercado a 24 meses, ya verás.”" },
    ],
  },
};

// ============================================================================
// Pricing math — DETERMINISTIC and SHARED between public and admin.
// Any change here is automatically picked up by both flows.
// ============================================================================

// FNV-1a 32-bit → [0, 1). Stable across browsers/Node.
export function hash01(str) {
  const s = String(str || "default");
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

// Per-plan ¢/kWh — base rate + deterministic ZIP+TDSP variance (±0.8¢)
// + service-type surcharge (commercial +2.5¢ / apartments +0.4¢).
export function dynamicRate({ planId, baseRate, zip, tdsp, serviceType }) {
  const seed = `${zip || "75001"}|${tdsp || "Oncor"}|${planId}`;
  const r = hash01(seed);
  const variance = (r - 0.5) * 1.6; // ±0.8¢
  const surcharge =
    serviceType === "commercial" ? 2.5 :
    serviceType === "apartments" ? 0.4 :
    0;
  return Math.max(8.5, Math.min(18.0, baseRate + variance + surcharge));
}

// Zone-average ¢/kWh — used as a context anchor "Promedio zona ~15.2¢".
export function zoneAverageRate({ zip, tdsp, serviceType }) {
  const seed = `${zip || "75001"}|${tdsp || "Oncor"}|zone|${serviceType || "residential"}`;
  const r = hash01(seed);
  const base = 14.5 + r * 3.0;
  const nudge =
    serviceType === "commercial" ? 1.2 :
    serviceType === "apartments" ? 0.5 :
    0;
  return Math.max(12.5, Math.min(19.5, base + nudge));
}

// Compute all 3 plan pricings, then apply the Secure 24 floor: it must be
// at least $15/mes above the most expensive 12-month plan. Otherwise the
// 24-month tier can come up cheaper in low-variance ZIPs.
//
// Returns: [{ plan, planRate, monthly, usage }] in PLAN_BASE order.
export function computePricings({ zip, tdsp, serviceType }) {
  const usage = SERVICE_USAGE_KWH[serviceType] || 2000;
  const rows = PLAN_BASE.map((plan) => {
    const rate = dynamicRate({ planId: plan.id, baseRate: plan.baseRate, zip, tdsp, serviceType });
    return {
      plan,
      planRate: rate,
      monthly: Math.round((rate / 100) * usage),
      usage,
    };
  });
  const max12 = Math.max(
    0,
    ...rows.filter((r) => r.plan.id === "saver12" || r.plan.id === "eco12").map((r) => r.monthly),
  );
  const secure = rows.find((r) => r.plan.id === "secure24");
  if (secure && max12 > 0 && secure.monthly < max12 + 15) {
    const targetMonthly = max12 + 15;
    secure.monthly = targetMonthly;
    secure.planRate = (targetMonthly / usage) * 100;
  }
  return rows;
}

// Compute pricing for a single picked plan — used by ServicePlacement.jsx's
// summary (Step 4-6) so the global "Estimated $/mes" matches Step 3 cards.
// Honors the same Secure 24 floor.
export function pricingForPickedPlan({ planId, zip, tdsp, serviceType }) {
  const rows = computePricings({ zip, tdsp, serviceType });
  return rows.find((r) => r.plan.id === planId) || null;
}
